import { useEffect, useMemo, useState } from 'react';
import { isEmpty, isEqual, toString } from 'lodash';
import { valuationsAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useAllocationVersionQueryString } from 'pages/ValuationsAllocation/hooks';
import { UseAllocationVersionQueryString, UseCompanyMeasurementDateFilter } from 'pages/ValuationsAllocation/types';
import { useCompanyMeasurementDateFilter } from 'services/hooks';
import {
  GetPublicCompsIndexesByDateParams,
  useGetFundInvestmentByAllocation,
  useGetPublicCompsIndexesByDate,
  useUpdateFundInvestmentDetails,
} from 'services/hooks/fundInvestments';
import { useGetValuationByAllocation, useUpdateValuation } from 'services/hooks/valuations';
import { useAllocationValuesStore } from 'store';
import { getObjectValue } from 'utilities';
import { UseFundInvestmentValuationAllocationReturn } from './useFundInvestmentValuationAllocation.types';

const useFundInvestmentValuationAllocation = (): UseFundInvestmentValuationAllocationReturn => {
  const [storeValue, dispatch] = useStore() as UseStoreValues;
  const { valuationInfo: currentValuation } = storeValue;
  const { allocation: currentAllocation } = getObjectValue(currentValuation);

  // Allocation values store
  const currentAllocationId = useAllocationValuesStore(state => state.currentAllocationId);
  const setCurrentAllocationId = useAllocationValuesStore(state => state.setCurrentAllocationId);

  // Fund investment by allocation
  const {
    data: fundInvestmentByAllocation,
    isFetching: isFetchingFundInvestmentByAllocation,
    isLoading: isRetrievingFundInvestmentByAllocation,
  } = useGetFundInvestmentByAllocation({
    allocationId: currentAllocationId,
    shouldQueryAutomatically: Boolean(currentAllocationId),
  });

  // Valuation by allocation
  const {
    data: valuationByAllocation,
    isFetching: isFetchingValuationByAllocation,
    isLoading: isRetrievingValuationByAllocation,
    refetch: getValuationByAllocation,
  } = useGetValuationByAllocation({
    allocationId: currentAllocationId,
    shouldQueryAutomatically: Boolean(currentAllocationId),
  });

  // Update fund investment details
  const { mutateAsync: updateFundInvestmentDetails, isLoading: isUpdatingFundInvestmentDetails } =
    useUpdateFundInvestmentDetails();

  // Update valuation
  const { mutateAsync: updateValuation, isLoading: isUpdatingValuation } = useUpdateValuation();

  // Public comps by date
  const [publicCompsIndexesByDateParams, setPublicCompsIndexesByDateParams] =
    useState<GetPublicCompsIndexesByDateParams | null>(null);

  const { data: publicCompsByDate, isFetching: isFetchingPublicCompsByDate } = useGetPublicCompsIndexesByDate({
    ...(!isEmpty(publicCompsIndexesByDateParams) && publicCompsIndexesByDateParams),
    shouldQueryAutomatically: !isEmpty(publicCompsIndexesByDateParams),
  });

  // Measurement date
  const { MDDialogue: MeasurementDateDialog, selectedMeasurementDate } = (
    useCompanyMeasurementDateFilter as UseCompanyMeasurementDateFilter
  )();
  const { date: currentMeasurementDate, id: currentMeasurementDateId } = getObjectValue(selectedMeasurementDate);

  const [shouldShowMarketDateOnFilter, setShouldShowMarketDateOnFilter] = useState<boolean>(true);
  const [shouldShowVersionFilter, setShouldShowVersionFilter] = useState<boolean>(false);

  // Allocation filtering and versioning
  const {
    dialogProps: allocationVersionDialogProps,
    isForbidden,
    marketDialogProps,
    selectedVersion: currentAllocationVersion,
  } = (useAllocationVersionQueryString as unknown as UseAllocationVersionQueryString)({
    selectedMeasurementDate,
    shouldShowMarketDateOnFilter,
    shouldShowVersionFilter,
  });
  // Market dialog
  const {
    id: currentAllocationVersionId,
    market_currency: versionMarketCurrency,
    market_date: versionMarketDate,
  } = getObjectValue(currentAllocationVersion);
  const { isMarketDialogOpen, marketCurrency, marketDate, setIsMarketDialogOpen, setMarketCurrency, setMarketDate } =
    marketDialogProps;

  const isLoadingFundInvestmentByAllocation = useMemo<boolean>(
    () => isRetrievingFundInvestmentByAllocation,
    [isRetrievingFundInvestmentByAllocation]
  );

  const isLoadingValuationByAllocation = useMemo<boolean>(
    () => isRetrievingValuationByAllocation,
    [isRetrievingValuationByAllocation]
  );

  const isCurrentAllocationAccessForbidden = useMemo<boolean>(
    () => isForbidden || isEqual(toString(currentAllocation), ERROR_403),
    [currentAllocation, isForbidden]
  );

  // Set valuation info
  useEffect(() => {
    if (valuationByAllocation && !isEmpty(valuationByAllocation)) {
      dispatch?.(valuationsAction?.setValuationInfo(valuationByAllocation));
    }

    return () => {
      dispatch?.(valuationsAction?.setValuationInfo(null));
    };
  }, [dispatch, valuationByAllocation]);

  // Synchronize allocation with current version
  useEffect(() => {
    if (currentAllocationId !== currentAllocationVersionId) {
      setCurrentAllocationId(currentAllocationVersionId);
    }
  }, [currentAllocationId, currentAllocationVersionId, setCurrentAllocationId]);

  return {
    allocationVersionDialogProps,
    currentAllocation,
    currentAllocationVersion,
    currentMeasurementDate,
    currentMeasurementDateId,
    fundInvestmentByAllocation,
    getValuationByAllocation,
    isCurrentAllocationAccessForbidden,
    isFetchingFundInvestmentByAllocation,
    isFetchingPublicCompsByDate,
    isFetchingValuationByAllocation,
    isLoadingFundInvestmentByAllocation,
    isLoadingValuationByAllocation,
    isMarketDialogOpen,
    isUpdatingFundInvestmentDetails,
    isUpdatingValuation,
    marketCurrency,
    marketDate,
    MeasurementDateDialog,
    publicCompsByDate,
    selectedMeasurementDate,
    setIsMarketDialogOpen,
    setMarketCurrency,
    setMarketDate,
    setPublicCompsIndexesByDateParams,
    setShouldShowMarketDateOnFilter,
    setShouldShowVersionFilter,
    updateFundInvestmentDetails,
    updateValuation,
    valuationByAllocation,
    versionMarketCurrency,
    versionMarketDate,
  };
};

export default useFundInvestmentValuationAllocation;
